import { faArrowAltToTop, faMoon, faSun, faUserCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppBar,
    Box,
    darken,
    Divider,
    Drawer,
    Fade,
    IconButton,
    lighten,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    styled,
    Toolbar,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

import colorLogo from '@/assets/img/logo-h-c.png';
import whiteLogo from '@/assets/img/logo-h-w.png';
import Image from '@/components/layouts/Image';
import { faBars, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { AuthenticatedUserContext, ChosenThemeActionContext, ChosenThemeContext, OpenIddictContext } from '@ngt/openiddict';
import React, { FunctionComponent, MouseEvent, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Outlet, Link as RouteLink, useLocation } from 'react-router-dom';
import ShowOnScroll from './ShowOnScroll';
import { AppSettings } from '@/AppSettings';
import ToggleFeedbackButton from '../buttons/ToggleFeedbackButton';

export interface ILink
{
    url: string;
    icon: any;
    newTab: boolean;
    route: boolean;
    title: string;
}

interface ILayoutProps {
    links?: ILink[];
    children?: ReactNode;
}

const drawerWidth = '240px';

const StyledAppBar = styled(AppBar)(({ theme}) => ({
    zIndex: theme.zIndex.drawer + 1
}));

const StyledToolbar = styled(Toolbar)(({ theme}) => ({
    background: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,
    minHeight: '57px !important'
}))

const StyledTitle = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        theme.palette.primary.main,
    textDecoration: 'none',
    textTransform: 'uppercase'
}))

const StyledTitleLink = styled(Link)(({theme}) => ({
    marginLeft: 'auto',
    marginRight: theme.spacing(2),
    textDecoration: 'none'
}))

const DefaultLayout: FunctionComponent<ILayoutProps> = ({ links, children }) => {
    const theme = useTheme();
    const { data: user, isAdmin } = useContext(AuthenticatedUserContext);
    const openIddict = useContext(OpenIddictContext);

    const showDrawer = !!links && links.length > 0;

    const location = useLocation();

    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
    }, [setChosenTheme, chosenTheme])

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [drawOpen, setDrawOpen] = React.useState(false);

    const handleDrawerOpen = () => {
        setDrawOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawOpen(false);
    };

    const activeLinkIndex = useMemo(() => {
        let i: number | undefined = undefined;
        let length: number = 0;

        if (!links || links.length === 0) {
            return i;
        }


        for (let ci = 0; ci < links.length; ci++) {
            const link = links[ci];

            if (location.pathname.startsWith(link.url) && link.url.length > length) {
                length = link.url.length;
                i = ci;
            }
        }

        return i;
    }, [links, location])

    const [routeLoaded, setRouteLoaded] = useState(false);
    const [routeProgress, setRouteProgress] = useState<number | undefined>(undefined);

    const drawEnabled = showDrawer;

    let returnToTop = (
        <ListItem 
            button 
            sx={{
                borderTop: `1px solid ${theme.palette.grey[300]}`,
                minHeight: '50px'
            }}
            onClick={() => { 
                window.scrollTo({ left: 0, top: 0, behavior: 'smooth'});
            }}
        >
            <ListItemIcon 
                sx={{
                    fontSize: '24px'
                }}
            >
                <FontAwesomeIcon fixedWidth icon={faArrowAltToTop} />
            </ListItemIcon>
            <ListItemText 
                primary="Return to Top" 
                sx={{
                    display: !drawOpen ? 'none' : undefined
                }} 
            />
        </ListItem>
    )
    
    returnToTop = (
        <ShowOnScroll sx={{mt: 'auto'}}>
            <Box>
                {
                    drawOpen ?
                        returnToTop :
                        <Tooltip title="Return to Top" placement="right">
                            <Box>
                                {returnToTop}
                            </Box>
                        </Tooltip>
                }
            </Box>
        </ShowOnScroll>
    );

    return (
        <Box 
            sx={{
                display: 'flex',
                width: '100%'
            }}
        >
            <StyledAppBar
                position="fixed"
            >
                <StyledToolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: theme.spacing(2),
                            //display: drawOpen ? 'none' : undefined,
                            //visibility: drawEnabled ? undefined : 'hidden'
                            display: drawOpen || !drawEnabled ? 'none' : undefined 
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={faBars} />
                    </IconButton>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerClose}
                        edge="start"
                        sx={{
                            marginRight: theme.spacing(2),
                            display: !drawOpen ? 'none' : undefined
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={faTimes} />
                    </IconButton>
                    <Box
                        sx={{
                            alignSelf: 'center',
                            lineHeight: 0
                        }}
                    >
                        <a href={import.meta.env.VITE_API_WEBSITE_URL}>
                            <Image
                                sx={{
                                    height: 56
                                }}
                                alt="TROG Cancer Research" 
                                src={ chosenTheme === 'dark' ? whiteLogo : colorLogo}
                            />
                        </a>
                    </Box>
                    <StyledTitleLink
                        //@ts-ignore-next-line
                        component={RouteLink}
                        to='/'
                    >
                        <StyledTitle 
                            variant="h6"
                        >
                            <strong>{openIddict?.name}</strong>
                        </StyledTitle>
                    </StyledTitleLink>
                    {
                        !openIddict.theme?.overrideTheme && (
                            <Tooltip 
                                title={chosenTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                            >
                                <IconButton
                                    aria-label="theme"
                                    color="inherit"
                                    onClick={changeTheme}
                                >
                                    <FontAwesomeIcon icon={chosenTheme === 'dark' ? faMoon : faSun } />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    {
                        !!user &&
                        AppSettings.sentry.dsn && 
                        AppSettings.sentry.enableReportABug && (
                            <ToggleFeedbackButton />
                        )
                    }
                    {
                        !!user && (
                            <>
                                <IconButton
                                    id="btn-auth"
                                    aria-label="account of current user"
                                    aria-controls="menu-auth"
                                    aria-haspopup="true"
                                    onClick={handleMenu}
                                    color="inherit">
                                    <FontAwesomeIcon icon={faUserCircle} />
                                </IconButton>
                                <Menu
                                    id="menu-auth"
                                    anchorEl={anchorEl}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    open={anchorEl?.id === 'btn-auth'}
                                    onClose={handleClose}
                                >
                                    <MenuItem>{user?.displayName ?? user?.userName}</MenuItem>
                                    <Divider />
                                    {isAdmin && <MenuItem component={RouteLink} to='/administration' onClick={handleClose}>Administration</MenuItem>}
                                    <MenuItem component={RouteLink} to="/account/password" onClick={handleClose}>Change Password</MenuItem>
                                    <MenuItem component={RouteLink} to="/account/logout">
                                        Logout
                                    </MenuItem>
                                </Menu>
                            </>
                        )
                    }
                </StyledToolbar>
            </StyledAppBar>
            {
                drawEnabled && (
                    <Drawer
                        variant="permanent"
                        sx={{
                            width: drawOpen ? drawerWidth : 0,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            transition: drawOpen ? 
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }) :
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.leavingScreen,
                                }),
                            overflowX: drawOpen ? 'hidden' : 'hidden',
                            [theme.breakpoints.up('sm')]: drawOpen ? 
                                undefined : 
                                {
                                    width: theme.spacing(8),
                                },
                            '& .MuiPaper-root': {
                                width: drawOpen ? drawerWidth : 0,
                                transition: drawOpen ? 
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen,
                                    }) :
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.leavingScreen,
                                    }),
                                overflowX: drawOpen ? 'hidden' : 'hidden',
                                [theme.breakpoints.up('sm')]: drawOpen ? 
                                    undefined : 
                                    {
                                        width: theme.spacing(8),
                                    },
                            }
                        }}
                        // classes={{
                        //     paper: clsx({
                        //         [classes.drawerOpen]: drawOpen,
                        //         [classes.drawerClose]: !drawOpen,
                        //     }),
                        // }}
                    >
                        <Box 
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: theme.spacing(0, 1),
                                // necessary for content to be below app bar
                                ...(theme.mixins.toolbar as any),
                                minHeight: '56px !important'
                            }}
                        />
                        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', height: '100%'}}>
                            {
                                links?.map((link, i) => {
                                    const active = i === activeLinkIndex;

                                    let linkComponent = null;
                                    if (link.newTab || !link.route) {
                                        linkComponent = (
                                            <ListItem 
                                                button 
                                                component="a" 
                                                href={link.url} 
                                                sx={{
                                                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                                                    minHeight: '50px'
                                                }} 
                                                key={i}
                                            >
                                                <ListItemIcon 
                                                    sx={{
                                                        fontSize: '24px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon fixedWidth icon={link.icon} />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={link.title} 
                                                    sx={{
                                                        display: !drawOpen ? 'none' : undefined
                                                    }} 
                                                />
                                            </ListItem>
                                        );
                                    }
                                    else {
                                        linkComponent = (
                                            <ListItem 
                                                button
                                                component={RouteLink}
                                                to={link.url}
                                                sx={{
                                                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                                                    minHeight: '50px',
                                                    background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined
                                                }} 
                                                key={i}
                                            >
                                                <ListItemIcon 
                                                    sx={{
                                                        fontSize: '24px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon fixedWidth icon={link.icon} />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={link.title} 
                                                    sx={{
                                                        display: !drawOpen ? 'none' : undefined
                                                    }} 
                                                />
                                            </ListItem>
                                        );
                                    }


                                    if (drawOpen) {
                                        return linkComponent;
                                    }

                                    return (
                                        <Tooltip title={link.title} placement="right" key={i}>
                                            <Box>
                                                {linkComponent}
                                            </Box>
                                        </Tooltip>
                                    );
                                })
                            }
                            {returnToTop}
                        </List>
                    </Drawer>
                )
            }
            <Box 
                sx={{
                    flexGrow: 1,
                    padding: theme.spacing(3),
                    width: '80%'
                }}
            >
                <Box 
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: theme.spacing(0, 1),
                        // necessary for content to be below app bar
                        ...(theme.mixins.toolbar as any),
                        minHeight: '56px !important'
                    }}
                />
                { 
                    routeProgress !== undefined && (
                        <Box
                            sx={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                top: '64px',
                                '@media (min-width: 0px)': {
                                    minHeight: '48px'
                                },
                                '@media (min-width: 600px)': {
                                    minHeight: '64px'
                                }
                            }}
                        >
                            <LinearProgress variant="determinate" color="secondary" value={routeProgress}  />
                        </Box>
                    ) 
                }
                {/* <Fade appear in={routeLoaded}> */}
                <Fade appear in={true}>
                    <div>
                        <Outlet />
                        {children}
                    </div>
                </Fade>
            </Box>
        </Box>
    );
};

export default DefaultLayout;